import _ from "lodash";

import { AnyUser } from "@megaron/access-control";
import { HttpAction } from "@megaron/http-service";
import { HandlerMap } from "@megaron/http-service-host";
import { Serializer, Serializers } from "@megaron/serializers";

import { DiffSyncBaseSchema, DiffSyncSchema } from "./schema";

export type DiffDto<
  T extends DiffSyncBaseSchema,
  TChannel extends keyof T & string = keyof T & string,
> = TChannel extends keyof T & string
  ? {
      diffId: string;
      uniqueKeys: string[];
      channel: TChannel;
      local: T[TChannel] | null;
      remote: T[TChannel] | null;
      error: string | null;
      isPushable: boolean;
      isPullable: boolean;
    }
  : never;

export type DiffChannelDto<
  T extends DiffSyncBaseSchema,
  TChannel extends keyof T & string = keyof T & string,
> = {
  channel: TChannel;
  name?: string;
  uri?: string;
  remoteResourceUrl?: string;
};

type SchemaEntry<T extends DiffSyncBaseSchema, TChannel extends keyof T & string> = [
  TChannel,
  Serializer<T[TChannel]>,
];
const schemaEntries = <T extends DiffSyncBaseSchema>(schema: DiffSyncSchema<T>): SchemaEntry<T, keyof T & string>[] =>
  _.entries(schema) as any;

export const diffDtoSerialzier = <T extends DiffSyncBaseSchema>(schema: DiffSyncSchema<T>) =>
  Serializers.first(
    ...schemaEntries(schema).map(([channel, serializer]) =>
      Serializers.object<DiffDto<T, keyof T & string>>({
        diffId: Serializers.string,
        uniqueKeys: Serializers.string.array(),
        channel: Serializers.stringOneOf(channel),
        local: Serializers.nullable(serializer),
        remote: Serializers.nullable(serializer),
        error: Serializers.string.nullable(),
        isPushable: Serializers.boolean,
        isPullable: Serializers.boolean,
      } as any),
    ),
  );

export const diffChannelDtoSerializer = <T extends DiffSyncBaseSchema>(schema: DiffSyncSchema<T>) =>
  Serializers.object<DiffChannelDto<T, keyof T & string>>({
    channel: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
    name: Serializers.string.optional(),
    uri: Serializers.string.optional(),
    remoteResourceUrl: Serializers.string.optional(),
  });

export const diffSyncActions = <T extends DiffSyncBaseSchema>(schema: DiffSyncSchema<T>) => ({
  diffChannelQuery: HttpAction({
    path: "/diffSync/channel",
    method: "get",
    qsSerializer: Serializers.object({
      channel: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
    }),
    requiresAuth: true as const,
    valueSerializer: diffChannelDtoSerializer(schema),
  }),

  diffQuery: HttpAction({
    path: "/diffSync/diff",
    method: "get",
    qsSerializer: Serializers.object({
      channel: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
      isPushable: Serializers.booleanString,
      isPullable: Serializers.booleanString,
    }).partial(),
    requiresAuth: true as const,
    valueSerializer: diffDtoSerialzier(schema).array(),
  }),

  refreshDiffs: HttpAction({
    path: "/diffSync/refresh",
    method: "post",
    qsSerializer: Serializers.object({
      channel: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
    }).partial(),
    requiresAuth: true as const,
  }),

  pullDiffs: HttpAction({
    path: "/diffSync/pull",
    method: "post",
    bodySerializer: Serializers.object({
      channel: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
      diffIds: Serializers.string.array().optional(),
    }),
    requiresAuth: true as const,
  }),

  pushDiffs: HttpAction({
    path: "/diffSync/push",
    method: "post",
    bodySerializer: Serializers.object({
      channel: Serializers.stringOneOf<keyof T & string>(...Object.keys(schema)),
      diffIds: Serializers.string.array().optional(),
    }),
    requiresAuth: true as const,
  }),
});

export type DiffSyncHttpHandlers<T extends DiffSyncBaseSchema> = HandlerMap<
  ReturnType<typeof diffSyncActions<T>>,
  AnyUser
>;
